export type MarketingPhotoScope =
  | 'marketing'
  | 'brand_editorial'
  | 'onboarding'
  | 'public_explore'
  | 'public_share'
  | 'private_itinerary';

export interface MarketingPhotoAsset {
  id: string;
  title: string;
  url: string;
  alt: string;
  source: 'licensed_public' | 'paid_stock' | 'roam_owned';
  status: 'temporary_placeholder' | 'licensed' | 'approved';
  allowedScopes: MarketingPhotoScope[];
  credit: string;
  sourceUrl?: string;
  photographerUrl?: string;
  sourceWidth?: number;
  sourceHeight?: number;
  showInlineCredit?: boolean;
  licenseNote: string;
  replacementGuidance: string;
}

/**
 * Marketing image registry.
 *
 * The hero currently uses a temporary licensed-public image so the site is
 * photo-led during local QA. Before production marketing traffic, replace it
 * with a purchased/owned file under /assets/marketing and update the license
 * note + receipt location in docs/PHOTO-ASSET-REGISTER.md.
 */
export const LANDING_HERO_PHOTO: MarketingPhotoAsset = {
  id: 'landing-family-travel-hero-v0',
  title: 'Mother and daughter looking out from an airport hallway',
  url: 'https://images.pexels.com/photos/4173218/pexels-photo-4173218.jpeg?auto=compress&cs=tinysrgb&w=1200',
  alt: 'Mother and young daughter with luggage looking out from an airport hallway',
  source: 'licensed_public',
  status: 'approved',
  allowedScopes: ['marketing', 'brand_editorial'],
  credit: 'Photo by Gustavo Fring on Pexels',
  sourceUrl: 'https://www.pexels.com/photo/photo-of-woman-and-little-girl-in-the-airport-4173218/',
  photographerUrl: 'https://www.pexels.com/@gustavo-fring',
  sourceWidth: 5760,
  sourceHeight: 3840,
  showInlineCredit: false,
  licenseNote:
    'Pexels-sourced launch image. Pexels attribution is not required by the general license; keep Pexels/photographer provenance available internally and replace with paid stock or Roam-owned photography if brand/legal review prefers owned media.',
  replacementGuidance:
    'Preferred final asset: paid stock or Roam-owned editorial image of a real family traveling, wide landscape crop, no visible brand marks, warm but premium.',
};

export const ONBOARDING_PHOTOS = {
  auth: {
    id: 'onboarding-auth-london-family-view-v1',
    title: 'Family enjoying the view of Big Ben in London',
    url: 'https://images.pexels.com/photos/34154999/pexels-photo-34154999.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A family enjoying the view of Big Ben from the riverside in London.',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['marketing', 'brand_editorial', 'onboarding'],
    credit: 'Photo by Jacqueline Goncalves on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/family-enjoying-view-of-big-ben-in-london-34154999/',
    photographerUrl: 'https://www.pexels.com/@jacqueline-goncalves-1555636581',
    sourceWidth: 6263,
    sourceHeight: 4523,
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced auth/onboarding image. High-resolution travel-context photo; keep Pexels/photographer provenance available internally and do not imply this is a specific Roam customer family.',
    replacementGuidance:
      'Preferred final asset: crystal-clear premium travel image with parent/child context plus unmistakable destination or transit signal.',
  },
  verify: {
    id: 'onboarding-verify-airport-window-v0',
    title: 'Airport windows before a flight',
    url: 'https://images.pexels.com/photos/19821053/pexels-photo-19821053.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'View of Heathrow Airport tarmac and airplanes through terminal window during twilight.',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['brand_editorial', 'onboarding'],
    credit: 'Photo by Nunzio La Rosa on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/airplanes-behind-airport-windows-19821053/',
    photographerUrl: 'https://www.pexels.com/@nunzio-la-rosa-163886982',
    sourceWidth: 6240,
    sourceHeight: 4160,
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced verification image. Keep Pexels/photographer provenance available internally.',
    replacementGuidance:
      'Preferred final asset: parent and child in a calm airport or transit moment, wide crop, optimistic but practical.',
  },
  familyContext: {
    id: 'onboarding-family-city-walk-v0',
    title: 'Family walking through a city street',
    url: 'https://images.pexels.com/photos/7977934/pexels-photo-7977934.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A joyful family enjoying a walk outdoors on a sunny day, capturing family bonding.',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['brand_editorial', 'onboarding'],
    credit: 'Photo by Kindel Media on Pexels',
    sourceUrl:
      'https://www.pexels.com/photo/a-young-girl-in-pink-and-white-floral-dress-running-on-gray-concrete-pavement-7977934/',
    photographerUrl: 'https://www.pexels.com/@kindelmedia',
    sourceWidth: 6657,
    sourceHeight: 4438,
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced family context image. Keep Pexels/photographer provenance available internally.',
    replacementGuidance:
      'Preferred final asset: real family in an inspiring but believable city, park, or hotel transition moment.',
  },
  firstTrip: {
    id: 'onboarding-first-trip-collage-anchor-v0',
    title: 'Coastal family destination',
    url: 'https://images.pexels.com/photos/3155726/pexels-photo-3155726.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A family enjoys a sunny day at a luxurious overwater resort in a tropical paradise.',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['brand_editorial', 'onboarding'],
    credit: 'Photo by Asad Photo Maldives on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/green-palm-trees-and-white-gazebo-3155726/',
    photographerUrl: 'https://www.pexels.com/@asadphoto',
    sourceWidth: 6000,
    sourceHeight: 4000,
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced first-trip image. Keep Pexels/photographer provenance available internally.',
    replacementGuidance:
      'Preferred final asset: a destination-flexible family travel image that works beside the first-trip builder.',
  },
  importFlow: {
    id: 'onboarding-import-travel-notes-v0',
    title: 'Travel planning notes beside a map',
    url: 'https://images.pexels.com/photos/7368310/pexels-photo-7368310.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'Person planning a journey with a map, passport, and travel essentials on a wooden table.',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['brand_editorial', 'onboarding'],
    credit: 'Photo by Vlada Karpovich on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/a-person-holding-a-pen-7368310/',
    photographerUrl: 'https://www.pexels.com/@vlada-karpovich',
    sourceWidth: 6720,
    sourceHeight: 4480,
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced import-flow image. Keep Pexels/photographer provenance available internally.',
    replacementGuidance:
      'Preferred final asset: planning thread or notes transforming into a family travel plan, without fake place claims.',
  },
  exploreGate: {
    id: 'onboarding-explore-family-library-v0',
    title: 'Family travel discovery library',
    url: 'https://images.pexels.com/photos/29855986/pexels-photo-29855986.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A family enjoys a day outdoors in the lush landscape of Cagayan Valley, Philippines.',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['brand_editorial', 'onboarding', 'public_explore'],
    credit: 'Photo by SHARMAINE MONTICALBO on Pexels',
    sourceUrl:
      'https://www.pexels.com/photo/family-exploring-scenic-landscape-in-cagayan-valley-29855986/',
    photographerUrl: 'https://www.pexels.com/@mainesdtm',
    sourceWidth: 6000,
    sourceHeight: 4000,
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced Explore gate image. Keep Pexels/photographer provenance available internally.',
    replacementGuidance:
      'Preferred final asset: family discovering places from a premium travel library, broad enough for Explore upgrade messaging.',
  },
} satisfies Record<string, MarketingPhotoAsset>;

export const TRIP_START_COLLAGE: MarketingPhotoAsset[] = [
  {
    id: 'onboarding-category-city-v0',
    title: 'City adventure',
    url: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=1800&q=85',
    alt: 'A bright city street suited for a family city trip',
    source: 'licensed_public',
    status: 'temporary_placeholder',
    allowedScopes: ['brand_editorial', 'onboarding'],
    credit: 'Temporary licensed-public Unsplash placeholder',
    licenseNote:
      'Temporary visual placeholder. Replace with paid stock or Roam-owned category photography before production launch.',
    replacementGuidance: 'Preferred final asset: stroller-friendly city walk or iconic city scene.',
  },
  {
    id: 'onboarding-category-national-park-v0',
    title: 'Nature and parks',
    url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1800&q=85',
    alt: 'Mountain landscape for a nature-focused family trip',
    source: 'licensed_public',
    status: 'temporary_placeholder',
    allowedScopes: ['brand_editorial', 'onboarding'],
    credit: 'Temporary licensed-public Unsplash placeholder',
    licenseNote:
      'Temporary visual placeholder. Replace with paid stock or Roam-owned category photography before production launch.',
    replacementGuidance:
      'Preferred final asset: national park scene with family-safe trail context.',
  },
  {
    id: 'onboarding-category-food-culture-v0',
    title: 'Food and culture',
    url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1800&q=85',
    alt: 'A colorful table of food for a food and culture trip',
    source: 'licensed_public',
    status: 'temporary_placeholder',
    allowedScopes: ['brand_editorial', 'onboarding'],
    credit: 'Temporary licensed-public Unsplash placeholder',
    licenseNote:
      'Temporary visual placeholder. Replace with paid stock or Roam-owned category photography before production launch.',
    replacementGuidance:
      'Preferred final asset: real travel food moment that feels parent-friendly, not restaurant-specific unless verified.',
  },
];

/**
 * Destination cover photos for the seeded public snapshots in
 * convex/seedData.ts. Pexels licenses don't require attribution but we
 * track provenance here so we can swap to Roam-owned/Kiersten-licensed
 * media without losing source history.
 *
 * If you change a coverImage URL in seedData.ts, also update the matching
 * entry below (or remove it if the destination is now using Roam-owned media).
 */
export const SEED_DESTINATION_PHOTOS: MarketingPhotoAsset[] = [
  {
    id: 'seed-athens-cover-v0',
    title: 'Parthenon and Athens cityscape',
    url: 'https://images.unsplash.com/photo-1603565816030-6b389eeb23cb?auto=format&fit=crop&w=1600&q=80',
    alt: 'The Parthenon and Acropolis above Athens, Greece',
    source: 'licensed_public',
    status: 'temporary_placeholder',
    allowedScopes: ['public_explore', 'public_share', 'brand_editorial'],
    credit: 'Temporary licensed-public Unsplash placeholder',
    showInlineCredit: true,
    licenseNote:
      'Unsplash-sourced public seed placeholder. Keep provenance tracked and replace with paid stock or Roam-owned Athens imagery before broad public launch.',
    replacementGuidance:
      'Replace with licensed Kiersten/Roam-owned Athens family photography when available.',
  },
  {
    id: 'seed-big-sur-cover-v0',
    title: 'Big Sur coastal mountains',
    url: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1600&q=80',
    alt: 'Layered coastal mountains and road-trip scenery near Big Sur, California',
    source: 'licensed_public',
    status: 'temporary_placeholder',
    allowedScopes: ['public_explore', 'public_share', 'brand_editorial'],
    credit: 'Temporary licensed-public Unsplash placeholder',
    showInlineCredit: true,
    licenseNote:
      'Unsplash-sourced public seed placeholder. Keep provenance tracked and replace with paid stock or Roam-owned Big Sur imagery before broad public launch.',
    replacementGuidance:
      'Replace with licensed Kiersten/Roam-owned Big Sur family road-trip photography when available.',
  },
  {
    id: 'seed-yosemite-cover-v1',
    title: 'Yosemite Valley with iconic rock formations and dramatic clouds',
    url: 'https://images.pexels.com/photos/20401503/pexels-photo-20401503.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A breathtaking landscape view of Yosemite Valley with iconic rock formations under dramatic clouds on a spring day',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['public_explore', 'public_share', 'brand_editorial'],
    credit: 'Photo by Stephen Leonardi on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/yosemite-national-park-20401503/',
    photographerUrl: 'https://www.pexels.com/@stephenleo1982/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with licensed Kiersten/Roam-owned Yosemite photography when available.',
  },
  {
    id: 'seed-nyc-cover-v1',
    title: 'Family riverside stroll with NYC skyline backdrop',
    url: 'https://images.pexels.com/photos/21854006/pexels-photo-21854006.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: "A family enjoys a riverside stroll with New York City's iconic skyline as the backdrop",
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['public_explore', 'public_share', 'brand_editorial'],
    credit: 'Photo by Ian Ramírez on Pexels',
    sourceUrl:
      'https://www.pexels.com/photo/family-looking-at-skyscrapers-in-skyline-of-new-york-city-in-usa-21854006/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with licensed Kiersten/Roam-owned NYC family photography when available.',
  },
  {
    id: 'seed-disney-cover-v1',
    title: 'Iconic Cinderella Castle at Disney World',
    url: 'https://images.pexels.com/photos/33957642/pexels-photo-33957642.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'Cinderella Castle at Walt Disney World Magic Kingdom against a blue sky',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['public_explore', 'public_share', 'brand_editorial'],
    credit: 'Photo by Abhishek Naviakha on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/iconic-cinderella-castle-at-disney-world-33957642/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking. Note Disney trademarks; use only as launch placeholder, prefer non-trademarked or generic family-park imagery long-term.',
    replacementGuidance:
      'Replace with licensed Kiersten/Roam-owned theme-park imagery when available; consider generic kid-park scenes to avoid Disney trademark exposure on public marketing surfaces.',
  },
];

/**
 * Category fallback covers for trip-item planning tiles when the underlying
 * activity has no own imageUrl. Each entry maps to one of the categories
 * resolved by `categoryForConvexItem` in the trip workspace. Photos were
 * sourced from Pexels (license does not require attribution). If a tile
 * already has a real place photo, that wins — these are last-resort visuals
 * so day boards never feel sparse.
 */
export const CATEGORY_FALLBACK_PHOTOS: MarketingPhotoAsset[] = [
  {
    id: 'tile-fallback-dining-family-table-v0',
    title: 'Family enjoying a meal together at a dining table',
    url: 'https://images.pexels.com/photos/8507683/pexels-photo-8507683.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A family eating a meal together at a warmly lit dining table',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo by Jep Gambardella on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/8507683/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with owned family-dining photography when available. Should feel warm and unstaged.',
  },
  {
    id: 'tile-fallback-dining-outdoor-v0',
    title: 'Family at an outdoor restaurant table',
    url: 'https://images.pexels.com/photos/17166783/pexels-photo-17166783.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'Top-down view of a family eating at an outdoor restaurant table with a baby in a stroller',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/17166783/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Swap for owned al-fresco family dining photo when brand library is ready.',
  },
  {
    id: 'tile-fallback-outdoor-trail-v0',
    title: 'Family hiking on a mountain trail',
    url: 'https://images.pexels.com/photos/13004440/pexels-photo-13004440.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A family enjoying a leisurely hike on a scenic mountain trail under a bright blue sky',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/13004440/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with owned family-on-trail photography when available; prefer kid-paced trails.',
  },
  {
    id: 'tile-fallback-outdoor-forest-v0',
    title: 'Mother and child walking on a forest trail',
    url: 'https://images.pexels.com/photos/5630420/pexels-photo-5630420.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A mother and child walk together on a forest trail surrounded by lush greenery',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/5630420/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned woodland/family-walk photo when brand library is ready.',
  },
  {
    id: 'tile-fallback-attraction-museum-art-v0',
    title: 'Child engaging with art in a modern gallery',
    url: 'https://images.pexels.com/photos/30838550/pexels-photo-30838550.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A young boy pointing at artwork in a modern art gallery',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/30838550/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Swap for owned museum/gallery scene with a child when brand library is ready.',
  },
  {
    id: 'tile-fallback-attraction-museum-family-v0',
    title: 'Family exploring an art exhibition',
    url: 'https://images.pexels.com/photos/13432768/pexels-photo-13432768.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A family exploring an art exhibition in a modern gallery with framed artworks',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/13432768/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned cultural attraction photography when available.',
  },
  {
    id: 'tile-fallback-hotel-room-v0',
    title: 'Bright hotel room with morning light',
    url: 'https://images.pexels.com/photos/36162677/pexels-photo-36162677.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'An inviting hotel room interior with a comfortable bed and soft morning light through the window',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/36162677/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with owned premium-but-warm hotel room photography when brand library is ready.',
  },
  {
    id: 'tile-fallback-hotel-modern-v0',
    title: 'Modern hotel room with city view',
    url: 'https://images.pexels.com/photos/34040619/pexels-photo-34040619.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A modern hotel room with large windows overlooking a cityscape and stylish decor',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/34040619/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Replace with owned premium urban hotel photography when available.',
  },
  {
    id: 'tile-fallback-transit-airplane-window-v0',
    title: 'View through an airplane window',
    url: 'https://images.pexels.com/photos/22388078/pexels-photo-22388078.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A scenic landscape beneath a clear blue sky seen through an airplane window',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/22388078/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with owned transit/in-flight photo when brand library is ready. Optimistic, not stressed.',
  },
  {
    id: 'tile-fallback-break-pool-float-v0',
    title: 'Dad and kids on a pool float',
    url: 'https://images.pexels.com/photos/11266606/pexels-photo-11266606.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A dad and kids laughing and bonding on a pool float in a sunny lush garden',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo by Kindel Media on Pexels',
    sourceUrl: 'https://www.pexels.com/photo/11266606/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with owned reset/pool-break photography when brand library is ready.',
  },
  {
    id: 'tile-fallback-break-resort-pool-v0',
    title: 'Relaxing day at a hotel pool',
    url: 'https://images.pexels.com/photos/16042557/pexels-photo-16042557.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'Guests enjoying a relaxing summer day at a hotel outdoor swimming pool',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/16042557/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned warm-light pool/spa imagery when available.',
  },
  {
    id: 'tile-fallback-beach-family-ocean-v0',
    title: 'Family running into the ocean',
    url: 'https://images.pexels.com/photos/8623946/pexels-photo-8623946.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A family running into the ocean on a sunny beach day in Portugal',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/8623946/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Replace with owned family-beach photography when brand library is ready.',
  },
  {
    id: 'tile-fallback-beach-kids-shore-v0',
    title: 'Family at the beach with cityscape backdrop',
    url: 'https://images.pexels.com/photos/35659774/pexels-photo-35659774.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A happy family enjoys a sunny day at the beach with waves and cityscape in the background',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/35659774/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Replace with owned beach-day photography when available.',
  },
  {
    id: 'tile-fallback-play-playground-swing-v0',
    title: 'Child smiling on a playground swing',
    url: 'https://images.pexels.com/photos/9870338/pexels-photo-9870338.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A happy young girl smiling on a green swing at a vibrant playground',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/9870338/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Replace with owned playground/play-moment photography when brand library is ready.',
  },
];

/**
 * Extended destination cover catalog beyond the public-snapshot seeds. Used by
 * `destinationCoverForName` to give every private trip a destination-flavored
 * hero image when the user starts a trip in one of these places. Add new
 * entries here (with verified-200 Pexels URLs) when expanding cover support.
 */
export const EXTENDED_DESTINATION_PHOTOS: MarketingPhotoAsset[] = [
  {
    id: 'dest-cover-paris-eiffel-v0',
    title: 'Paris cityscape with the Eiffel Tower',
    url: 'https://images.pexels.com/photos/4456347/pexels-photo-4456347.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A serene Paris cityscape featuring the iconic Eiffel Tower under a cloudy sky',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/4456347/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Swap for owned Paris family-travel photography when available; keep landscape framing.',
  },
  {
    id: 'dest-cover-london-bigben-v0',
    title: 'Big Ben at sunset in London',
    url: 'https://images.pexels.com/photos/17193910/pexels-photo-17193910.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'The iconic Big Ben tower bathed in the warm glow of sunset in London, England',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/17193910/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned London family-travel photography when available.',
  },
  {
    id: 'dest-cover-tokyo-tower-v0',
    title: 'Tokyo skyline with Tokyo Tower',
    url: 'https://images.pexels.com/photos/27164620/pexels-photo-27164620.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'Stunning view of the Tokyo skyline with skyscrapers and the iconic Tokyo Tower',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/27164620/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Tokyo family-travel photography when available.',
  },
  {
    id: 'dest-cover-rome-colosseum-v0',
    title: 'The Colosseum in Rome',
    url: 'https://images.pexels.com/photos/32472809/pexels-photo-32472809.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'The majestic Colosseum in Rome, a symbol of ancient Roman engineering and architecture',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/32472809/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Rome family-travel photography when available.',
  },
  {
    id: 'dest-cover-barcelona-park-guell-v0',
    title: 'Barcelona with Park Güell and Sagrada Familia',
    url: 'https://images.pexels.com/photos/14948886/pexels-photo-14948886.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: "Scenic view of Barcelona with Park Güell's architecture and Sagrada Familia in the background",
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/14948886/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Barcelona family-travel photography when available.',
  },
  {
    id: 'dest-cover-amsterdam-canal-v0',
    title: 'Amsterdam canal scene',
    url: 'https://images.pexels.com/photos/1258865/pexels-photo-1258865.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A charming Amsterdam canal scene with classic Dutch architecture and a boat cruising on the water',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/1258865/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Amsterdam family-travel photography when available.',
  },
  {
    id: 'dest-cover-lisbon-rooftops-v0',
    title: 'Lisbon red rooftops',
    url: 'https://images.pexels.com/photos/33114413/pexels-photo-33114413.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'The charming red-roofed architecture of Lisbon against a bright blue sky',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/33114413/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Lisbon family-travel photography when available.',
  },
  {
    id: 'dest-cover-hawaii-coastline-v0',
    title: 'Aerial Hawaiian coastline',
    url: 'https://images.pexels.com/photos/4321834/pexels-photo-4321834.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A stunning aerial view of a vibrant Hawaiian coastline with lush mountains and crystal-clear waters',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/4321834/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance:
      'Swap for owned Hawaii family-travel photography when available. Keep aerial/landscape framing.',
  },
  {
    id: 'dest-cover-iceland-lava-field-v0',
    title: 'Iceland moss-covered lava field',
    url: 'https://images.pexels.com/photos/33931095/pexels-photo-33931095.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A scenic moss-covered lava field in Iceland',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/33931095/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Iceland family-travel photography when available.',
  },
  {
    id: 'dest-cover-cancun-palapas-v0',
    title: 'Tropical beach with palapas in Cancún',
    url: 'https://images.pexels.com/photos/5489654/pexels-photo-5489654.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A serene tropical beach scene with palapas and clear blue waters',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/5489654/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Cancún/Riviera Maya family-travel photography.',
  },
  {
    id: 'dest-cover-bali-rice-terraces-v0',
    title: 'Bali rice terraces',
    url: 'https://images.pexels.com/photos/7980413/pexels-photo-7980413.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: "A scenic view of verdant terraced rice fields and palm trees in Bali's rural countryside",
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/7980413/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Bali family-travel photography when available.',
  },
  {
    id: 'dest-cover-banff-mountains-v0',
    title: 'Banff National Park mountains',
    url: 'https://images.pexels.com/photos/28365756/pexels-photo-28365756.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A scenic mountain landscape with evergreen trees in Banff National Park, Alberta, Canada',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/28365756/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Banff/Canadian Rockies family-travel photography.',
  },
  {
    id: 'dest-cover-grand-canyon-v0',
    title: 'Grand Canyon West',
    url: 'https://images.pexels.com/photos/13641027/pexels-photo-13641027.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'A breathtaking landscape of Grand Canyon West under a clear blue sky',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/13641027/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Grand Canyon family-travel photography when available.',
  },
  {
    id: 'dest-cover-sydney-harbour-v0',
    title: 'Sydney Harbour Bridge and Opera House',
    url: 'https://images.pexels.com/photos/783681/pexels-photo-783681.jpeg?auto=compress&cs=tinysrgb&w=2400',
    alt: 'View of Sydney Harbour Bridge and Sydney Opera House on a sunny day',
    source: 'licensed_public',
    status: 'approved',
    allowedScopes: ['private_itinerary', 'brand_editorial', 'public_explore'],
    credit: 'Photo via Pexels',
    sourceUrl: 'https://www.pexels.com/photo/783681/',
    showInlineCredit: false,
    licenseNote:
      'Pexels-sourced. Pexels license does not require attribution; provenance kept here for replacement tracking.',
    replacementGuidance: 'Swap for owned Sydney family-travel photography when available.',
  },
];
